import { useMemo } from "react"
import {
  useListGdeltBiothreatSignalsGdeltBiothreatSignalsGet,
  type GdeltGeoLevel,
  type ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams,
} from "@phc/common/gen/threatAnalyticsGen"
import type { GridSortModel } from "@mui/x-data-grid-pro"
import { usePrecomputedBiothreatSignals } from "./PrecomputedBiothreatSignalsContext"
import { ALL_DISEASES, ALL_GEO } from "./types"
import {
  useGdeltDateFilterParams,
  type GdeltBiothreatDateFilterParams,
} from "./usePrecomputedDateRangeParams"
import { mapSortModelToParams } from "./mapSortModelToParams"

const GDELT_SORT_FIELD_MAP: Record<string, string> = {
  publish_time: "most_recent_member_published_at",
  title: "description",
  cluster_count: "cluster_count",
  disease: "disease_code",
  resolved_location: "resolved_location",
  impact_score: "impact_score",
}

export const usePrecomputedGdeltSignals = ({
  page,
  sortModel,
  limit,
  impactScoreMinOverride,
}: {
  page: number
  sortModel: GridSortModel
  /** Override the page size (e.g. fetch the full filtered set for the map). */
  limit?: number
  /**
   * Override the impact-score floor for this query only. Pass `null` to omit the
   * floor entirely (the map fetches the full "noise" field this way, regardless
   * of the user's threshold). When omitted, the shared filter value is used.
   */
  impactScoreMinOverride?: number | null
}) => {
  const { gdeltFilters, pageSize } = usePrecomputedBiothreatSignals()
  const effectiveLimit = limit ?? pageSize
  const dateFilterParams: GdeltBiothreatDateFilterParams =
    useGdeltDateFilterParams()

  const params =
    useMemo((): ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams => {
      const sortParams = mapSortModelToParams(sortModel, GDELT_SORT_FIELD_MAP)
      const summarySearch = gdeltFilters.summarySearch.trim()

      let geo_level: GdeltGeoLevel | undefined
      let resolved_location: string | undefined
      if (gdeltFilters.state !== ALL_GEO) {
        geo_level = "state"
        resolved_location = gdeltFilters.state
      } else if (gdeltFilters.country !== ALL_GEO) {
        geo_level = "country"
        resolved_location = gdeltFilters.country
      }

      // `null` override = omit the floor (full noise field for the map);
      // `undefined` (not passed) = fall back to the shared filter value.
      const impact_score_min =
        impactScoreMinOverride !== undefined
          ? (impactScoreMinOverride ?? undefined)
          : gdeltFilters.impactScoreMin
            ? Number(gdeltFilters.impactScoreMin)
            : undefined

      return {
        disease_code:
          gdeltFilters.disease !== ALL_DISEASES
            ? gdeltFilters.disease
            : undefined,
        geo_level,
        resolved_location,
        ...dateFilterParams,
        title_search: gdeltFilters.titleSearch.trim() || undefined,
        member_title_search: gdeltFilters.memberTitleSearch.trim() || undefined,
        summary_search: summarySearch || undefined,
        has_impact_score: true,
        impact_score_min,
        ...sortParams,
        limit: effectiveLimit,
        offset: page * effectiveLimit,
      }
    }, [
      gdeltFilters,
      page,
      effectiveLimit,
      dateFilterParams,
      sortModel,
      impactScoreMinOverride,
    ])

  return useListGdeltBiothreatSignalsGdeltBiothreatSignalsGet(params, {
    query: { placeholderData: previousData => previousData },
  })
}
